import { useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "contexts/alert";
import {
  generateInsightsReport,
  getInsightsErrorMessage,
  getInsightsReportCatalog,
  IInsightsReportCatalogItem,
} from "v2/services/insights.service";
import { downloadCsvReport } from "../downloadCsvReport";
import { downloadHtmlReport } from "../downloadHtmlReport";
import { CONDUCCO_PRIMARY, INSIGHTS_FONT } from "../tokens";
import { ReportCard } from "./ReportCard";

export interface ReportCatalogProps {
  workspaceId: string;
}

/**
 * A aba de relatórios.
 *
 * A lista vem inteira do backend (`GET /insights/reports`): id, título,
 * descrição, parâmetros e disponibilidade por workspace. Não há catálogo
 * paralelo aqui — relatório novo no backend aparece na tela sem alterar o front.
 *
 * O contrato é escolhido na faixa do painel (`ContractBar`), acima das abas:
 * ele vale para as duas, e ter um seletor por aba deixava o mesmo estado com
 * duas aparências.
 */
export function ReportCatalog({ workspaceId }: ReportCatalogProps) {
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const { openAlert } = useAlert();

  const {
    data: reports,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["insights-report-catalog", workspaceId],
    queryFn: () => getInsightsReportCatalog(workspaceId),
    enabled: Boolean(workspaceId),
    retry: false,
    // A disponibilidade depende de tabela existir no schema do workspace: muda
    // por deploy do Airflow, não por minuto.
    staleTime: 5 * 60 * 1000,
  });

  const handleGenerate = async (
    report: IInsightsReportCatalogItem,
    params: { cpfs?: string[]; desde?: string; ate?: string }
  ) => {
    setGeneratingId(report.id);
    try {
      const response = await generateInsightsReport(report.id, {
        workspace_id: workspaceId,
        ...params,
      });
      // Recusa com motivo (período sem exportação, resultado acima do teto):
      // não baixa nada e diz por quê. Vem com `disponivel: true` — não é erro,
      // e um arquivo vazio ou cortado seria pior que a frase.
      if (response.csvMotivo) {
        openAlert(response.csvMotivo, "warning");
        return;
      }

      // Relatório cujo entregável é o arquivo: baixa o CSV, não o HTML.
      if (response.csv && response.csvFilename) {
        downloadCsvReport(response.csv, response.csvFilename);
        openAlert("Relatório baixado", "success");
        return;
      }

      await downloadHtmlReport(response.html, response.filename);
      // Indisponível não é erro: o backend devolve 200 com o motivo dentro do
      // HTML (D7). Dizer "baixado" sem mais nada esconderia isso do usuário.
      if (response.disponivel === false) {
        openAlert(
          response.motivo ?? "Relatório baixado sem dados para este workspace",
          "warning"
        );
      } else {
        openAlert("Relatório baixado", "success");
      }
    } catch (err: any) {
      openAlert(
        getInsightsErrorMessage(err, "Serviço indisponível, tente novamente"),
        "error"
      );
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: { xs: 2, md: 4 },
        py: 3,
        fontFamily: INSIGHTS_FONT,
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} sx={{ color: CONDUCCO_PRIMARY }} />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ fontFamily: INSIGHTS_FONT }}>
            {getInsightsErrorMessage(
              error,
              "Não foi possível carregar os relatórios deste workspace."
            )}
          </Alert>
        )}

        {!isLoading && !isError && reports?.length === 0 && (
          <Alert severity="info" sx={{ fontFamily: INSIGHTS_FONT }}>
            Nenhum relatório publicado ainda.
          </Alert>
        )}

        {!isLoading && !isError && reports && reports.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gap: 1.75,
              // `auto-fill` com largura mínima em vez de contagem fixa por
              // breakpoint: o painel também abre dentro do drawer, e ali dois
              // cards por linha davam um card estreito e alto.
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(320px, 1fr))",
              },
              alignItems: "stretch",
            }}
          >
            {reports.map((report) => (
              <ReportCard
                // A chave inclui o workspace: trocar de workspace reinicia os
                // campos, em vez de levar o período de um contrato para outro.
                key={`${workspaceId}:${report.id}`}
                report={report}
                generating={generatingId === report.id}
                disabled={
                  !workspaceId || (generatingId !== null && generatingId !== report.id)
                }
                onGenerate={handleGenerate}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
